import { supabase } from '../lib/supabase';
import { storage } from '../data/localStorage';

const POINTS_SYSTEM = {
  LOGIN: 5,                 // Points for daily login
  CREATE_QUESTION: 10,      // Points for creating a question
  BULK_UPLOAD_BONUS: 25,    // Bonus points for bulk uploading questions
  GENERATE_EXAM: 20,        // Points for generating an exam
  SCHEDULE_EXAM: 15,        // Points for scheduling an exam
  LOGIN_STREAK_BONUS: 2,    // Additional points per day of login streak
  MILESTONE_COMPLETION: 50, // Points for completing milestones
};

// Badges configuration
const BADGES = {
  NOVICE: {
    id: 'novice',
    name: 'Novice',
    description: 'Just getting started',
    icon: 'FiStar',
    color: 'blue',
    required_points: 0
  },
  CONTRIBUTOR: {
    id: 'contributor',
    name: 'Contributor',
    description: 'Created at least 10 questions',
    icon: 'FiFileText',
    color: 'green',
    required_points: 100,
    required_questions: 10
  },
  EXPERT: {
    id: 'expert',
    name: 'Expert',
    description: 'Created at least 50 questions',
    icon: 'FiAward',
    color: 'purple',
    required_points: 500,
    required_questions: 50
  },
  MASTER: {
    id: 'master',
    name: 'Master',
    description: 'Created at least 100 questions',
    icon: 'FiTrophy',
    color: 'orange',
    required_points: 1000,
    required_questions: 100
  },
  GURU: {
    id: 'guru',
    name: 'Guru',
    description: 'Created at least 500 questions',
    icon: 'FiZap',
    color: 'pink',
    required_points: 5000,
    required_questions: 500
  },
  STREAK_MASTER: {
    id: 'streak_master',
    name: 'Streak Master',
    description: 'Maintained a 7-day login streak',
    icon: 'FiCalendar',
    color: 'indigo',
    required_points: 200,
    required_streak: 7
  }
};

export const userService = {
  // Get user stats
  async getUserStats(userId) {
    try {
      const { data, error } = await supabase
        .from('user_stats_fahim')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      
      if (data) {
        return data;
      } else {
        // Create user stats if they don't exist
        const newStats = {
          user_id: userId,
          questions_created: 0,
          exams_generated: 0,
          total_points: 0,
          streak_days: 0,
          show_on_leaderboard: true,
          last_login: new Date().toISOString()
        };
        
        const { data: createdStats, error: createError } = await supabase
          .from('user_stats_fahim')
          .insert(newStats)
          .select()
          .single();
          
        if (createError) throw createError;
        return createdStats;
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Fallback to localStorage
      return storage.getUserStatsByUserId(userId);
    }
  },
  
  // Update user stats
  async updateUserStats(userId, stats) {
    try {
      const { data, error } = await supabase
        .from('user_stats_fahim')
        .update({
          ...stats,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user stats:', error);
      // Fallback to localStorage
      return storage.updateUserStats(userId, stats);
    }
  },
  
  // Toggle leaderboard visibility preference
  async toggleLeaderboardVisibility(userId, showOnLeaderboard) {
    try {
      const { data, error } = await supabase
        .from('user_stats_fahim')
        .update({ 
          show_on_leaderboard: showOnLeaderboard,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating leaderboard visibility:', error);
      // Fallback to localStorage
      return storage.updateUserStats(userId, { show_on_leaderboard: showOnLeaderboard });
    }
  },
  
  // Get leaderboard
  async getLeaderboard() {
    try {
      const { data, error } = await supabase
        .from('user_stats_fahim')
        .select(`
          id,
          user_id,
          questions_created,
          exams_generated,
          total_points,
          streak_days,
          profiles:user_id (
            full_name,
            avatar_url
          )
        `)
        .eq('show_on_leaderboard', true)
        .order('total_points', { ascending: false })
        .limit(50);
        
      if (error) throw error;
      
      // If no data or only a few entries, populate with mock data
      if (!data || data.length < 10) {
        return this.getMockLeaderboardData();
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      // Fallback to localStorage or mock data
      const localLeaderboard = storage.getLeaderboard();
      return localLeaderboard.length > 0 ? localLeaderboard : this.getMockLeaderboardData();
    }
  },
  
  // Get mock leaderboard data
  getMockLeaderboardData() {
    const mockUsers = [
      { id: 'mock-1', name: 'Jowati Juhary', points: 1250, questions: 78, streak: 12 },
      { id: 'mock-2', name: 'Kamarul Ariffin', points: 980, questions: 62, streak: 9 },
      { id: 'mock-3', name: 'Emily Abd Rahman', points: 875, questions: 54, streak: 7 },
      { id: 'mock-4', name: 'Erda Wati', points: 740, questions: 48, streak: 6 },
      { id: 'mock-5', name: 'Rita Wong', points: 690, questions: 42, streak: 8 },
      { id: 'mock-6', name: 'Norshima Zainal Shah', points: 580, questions: 35, streak: 5 },
      { id: 'mock-7', name: 'Ahmad Fahimi', points: 520, questions: 30, streak: 4 },
      { id: 'mock-8', name: 'Hilmi Azani', points: 450, questions: 27, streak: 3 },
      { id: 'mock-9', name: 'Surinder Kaur', points: 390, questions: 24, streak: 5 },
      { id: 'mock-10', name: 'Husna Mazlan', points: 340, questions: 20, streak: 4 },
      { id: 'mock-11', name: 'Hasrul Kamarulzaman', points: 290, questions: 18, streak: 3 },
      { id: 'mock-12', name: 'Ahmad Thamrini', points: 240, questions: 15, streak: 2 },
      { id: 'mock-13', name: 'Amzari Rani', points: 200, questions: 12, streak: 1 },
      { id: 'mock-14', name: 'Afifah Quraishah', points: 150, questions: 9, streak: 2 },
      { id: 'mock-15', name: 'Khadirah Ab Rahman', points: 100, questions: 6, streak: 1 }
    ];
    
    return mockUsers.map((user, index) => ({
      id: `mock-leaderboard-${index}`,
      user_id: user.id,
      questions_created: user.questions,
      exams_generated: Math.floor(user.questions / 3),
      total_points: user.points,
      streak_days: user.streak,
      profiles: {
        full_name: user.name,
        avatar_url: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=3b82f6&color=fff`
      }
    }));
  },
  
  // Get user badges
  async getUserBadges(userId) {
    try {
      const { data, error } = await supabase
        .from('user_badges_fahim')
        .select(`
          id,
          earned_at,
          badges:badge_id (
            id,
            name,
            description,
            icon,
            color,
            required_points
          )
        `)
        .eq('user_id', userId)
        .order('earned_at', { ascending: false });
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user badges:', error);
      // Fallback to localStorage
      return storage.getUserBadgesByUserId(userId);
    }
  },
  
  // Award achievement to user
  async awardAchievement(userId, achievement) {
    try {
      const { data, error } = await supabase
        .from('user_achievements_fahim')
        .insert({
          user_id: userId,
          achievement_type: achievement.type,
          achievement_name: achievement.name,
          achievement_description: achievement.description,
          achievement_icon: achievement.icon,
          points: achievement.points,
          earned_at: new Date().toISOString()
        })
        .select();
        
      if (error) throw error;
      
      // Update user's total points
      await this.updateUserStats(userId, {
        total_points: supabase.rpc('increment', { 
          row_id: userId,
          table: 'user_stats_fahim',
          column: 'total_points',
          value: achievement.points
        })
      });
      
      return data;
    } catch (error) {
      console.error('Error awarding achievement:', error);
      // Fallback to localStorage
      return storage.awardAchievement(userId, achievement);
    }
  },
  
  // Award login points and update streak
  async awardLoginPoints(userId) {
    try {
      // Get current stats
      const userStats = await this.getUserStats(userId);
      
      const today = new Date();
      const lastLogin = userStats.last_login ? new Date(userStats.last_login) : null;
      
      // Check if this is the first login of the day
      const isFirstLoginToday = !lastLogin || 
        (today.getDate() !== lastLogin.getDate() || 
         today.getMonth() !== lastLogin.getMonth() || 
         today.getFullYear() !== lastLogin.getFullYear());
      
      if (isFirstLoginToday) {
        // Check if the streak should continue or reset
        let currentStreak = userStats.streak_days || 0;
        
        if (lastLogin) {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          
          // If last login was yesterday, increment streak, otherwise reset
          const wasYesterday = 
            yesterday.getDate() === lastLogin.getDate() && 
            yesterday.getMonth() === lastLogin.getMonth() && 
            yesterday.getFullYear() === lastLogin.getFullYear();
          
          if (wasYesterday) {
            currentStreak += 1;
          } else {
            currentStreak = 1; // Reset streak
          }
        } else {
          currentStreak = 1; // First login ever
        }
        
        // Calculate points to award
        let pointsToAward = POINTS_SYSTEM.LOGIN;
        
        // Add streak bonus if applicable
        if (currentStreak > 1) {
          pointsToAward += POINTS_SYSTEM.LOGIN_STREAK_BONUS * currentStreak;
        }
        
        // Update user stats
        const updatedStats = await this.updateUserStats(userId, {
          streak_days: currentStreak,
          last_login: today.toISOString(),
          total_points: userStats.total_points + pointsToAward
        });
        
        // Award achievement for login
        await this.awardAchievement(userId, {
          type: 'login',
          name: 'Daily Login',
          description: `Logged in for day ${currentStreak} of your streak`,
          icon: 'FiCalendar',
          points: pointsToAward
        });
        
        // Check for streak milestone
        if (currentStreak === 7) {
          await this.awardBadge(userId, BADGES.STREAK_MASTER);
        }
        
        // Check for other badges
        await this.checkAndAwardBadges(userId);
        
        return {
          awarded: true,
          points: pointsToAward,
          currentStreak,
          updatedStats
        };
      }
      
      return {
        awarded: false,
        message: 'Already logged in today'
      };
    } catch (error) {
      console.error('Error awarding login points:', error);
      return { awarded: false, error: error.message };
    }
  },
  
  // Award points for creating a question
  async awardQuestionCreationPoints(userId, questionCount = 1) {
    try {
      // Get current stats
      const userStats = await this.getUserStats(userId);
      
      // Calculate points to award
      const pointsToAward = POINTS_SYSTEM.CREATE_QUESTION * questionCount;
      
      // Update user stats
      const updatedStats = await this.updateUserStats(userId, {
        questions_created: userStats.questions_created + questionCount,
        total_points: userStats.total_points + pointsToAward
      });
      
      // Award achievement
      await this.awardAchievement(userId, {
        type: 'question_creation',
        name: questionCount > 1 ? 'Bulk Question Creation' : 'Question Creation',
        description: `Created ${questionCount} new question${questionCount > 1 ? 's' : ''}`,
        icon: 'FiFileText',
        points: pointsToAward
      });
      
      // Add bonus for bulk uploads
      if (questionCount >= 5) {
        const bonusPoints = POINTS_SYSTEM.BULK_UPLOAD_BONUS;
        
        await this.updateUserStats(userId, {
          total_points: updatedStats.total_points + bonusPoints
        });
        
        await this.awardAchievement(userId, {
          type: 'bulk_upload',
          name: 'Bulk Upload Bonus',
          description: `Uploaded ${questionCount} questions at once`,
          icon: 'FiUpload',
          points: bonusPoints
        });
      }
      
      // Check for question count milestones and award badges
      await this.checkAndAwardBadges(userId);
      
      return {
        awarded: true,
        points: pointsToAward,
        updatedStats
      };
    } catch (error) {
      console.error('Error awarding question creation points:', error);
      return { awarded: false, error: error.message };
    }
  },
  
  // Award points for generating an exam
  async awardExamGenerationPoints(userId) {
    try {
      // Get current stats
      const userStats = await this.getUserStats(userId);
      
      // Calculate points to award
      const pointsToAward = POINTS_SYSTEM.GENERATE_EXAM;
      
      // Update user stats
      const updatedStats = await this.updateUserStats(userId, {
        exams_generated: userStats.exams_generated + 1,
        total_points: userStats.total_points + pointsToAward
      });
      
      // Award achievement
      await this.awardAchievement(userId, {
        type: 'exam_generation',
        name: 'Exam Generation',
        description: 'Generated a new exam paper',
        icon: 'FiFileText',
        points: pointsToAward
      });
      
      // Check for milestones
      await this.checkAndAwardBadges(userId);
      
      return {
        awarded: true,
        points: pointsToAward,
        updatedStats
      };
    } catch (error) {
      console.error('Error awarding exam generation points:', error);
      return { awarded: false, error: error.message };
    }
  },
  
  // Award a specific badge to the user
  async awardBadge(userId, badge) {
    try {
      // Check if user already has this badge
      const userBadges = await this.getUserBadges(userId);
      const alreadyHasBadge = userBadges.some(userBadge => 
        userBadge.badges?.id === badge.id
      );
      
      if (alreadyHasBadge) {
        return { awarded: false, message: 'Badge already awarded' };
      }
      
      // Award the badge
      const { error: badgeError } = await supabase
        .from('user_badges_fahim')
        .insert({
          user_id: userId,
          badge_id: badge.id,
          earned_at: new Date().toISOString()
        });
        
      if (badgeError) {
        console.error('Error awarding badge:', badgeError);
        // Fallback to localStorage
        storage.addUserBadge(userId, badge);
      }
      
      // Award milestone points
      await this.awardAchievement(userId, {
        type: 'badge_earned',
        name: `Badge Earned: ${badge.name}`,
        description: badge.description,
        icon: badge.icon,
        points: POINTS_SYSTEM.MILESTONE_COMPLETION
      });
      
      return { awarded: true, badge };
    } catch (error) {
      console.error('Error awarding badge:', error);
      // Fallback to localStorage
      storage.addUserBadge(userId, badge);
      return { awarded: false, error: error.message };
    }
  },
  
  // Check and award badges based on user stats
  async checkAndAwardBadges(userId) {
    try {
      // Get user's current stats
      const userStats = await this.getUserStats(userId);
      
      // Get user's current badges
      const userBadges = await this.getUserBadges(userId);
      const earnedBadgeIds = userBadges.map(ub => ub.badges?.id);
      
      // Define badges to check (in order of difficulty)
      const badgesToCheck = [
        BADGES.NOVICE,
        BADGES.CONTRIBUTOR,
        BADGES.EXPERT,
        BADGES.MASTER,
        BADGES.GURU,
        BADGES.STREAK_MASTER
      ];
      
      // Check each badge
      for (const badge of badgesToCheck) {
        // Skip if already earned
        if (earnedBadgeIds.includes(badge.id)) {
          continue;
        }
        
        let qualifies = userStats.total_points >= badge.required_points;
        
        // Additional requirements
        if (badge.required_questions && userStats.questions_created < badge.required_questions) {
          qualifies = false;
        }
        
        if (badge.required_streak && userStats.streak_days < badge.required_streak) {
          qualifies = false;
        }
        
        if (qualifies) {
          await this.awardBadge(userId, badge);
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error checking and awarding badges:', error);
      return { success: false, error: error.message };
    }
  },
  
  // Get points system constants
  getPointsSystem() {
    return POINTS_SYSTEM;
  },
  
  // Get badge definitions
  getBadgeDefinitions() {
    return BADGES;
  }
};